import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';

import { Auth } from 'src/providers/auth/auth';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { Events } from 'src/providers/events/events';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { GuestAccountService } from 'src/shared/services/guest-account.service';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';

import { Util } from 'src/shared/util/util';

const guestTypeMap: Record<string, string> = {
  consumer: 'regular',
  company: 'company',
  rental: 'rental'
};

@Injectable({
  providedIn: 'root'
})
export class CreateAccountGuard implements CanActivate {

  private auth = inject(Auth);
  private events = inject(Events);
  private guestAccountService = inject(GuestAccountService);
  private navCtrl = inject(CustomNavController);
  private onboardingProvider = inject(OnboardingProvider);
  private storage = inject(TidyStorage);
  private aboutThisPageService = inject(AboutThisPageService);
  private util = inject(Util);

  async canActivate(_route: ActivatedRouteSnapshot): Promise<boolean> {
    return true;
  }

  async handleAccountCreation(route: ActivatedRouteSnapshot): Promise<void> {
    const accountType = route.params['accountType'];
    const guestType = guestTypeMap[accountType] || 'regular';

    localStorage.setItem('isRentalClient', guestType === 'rental' ? 'true' : 'false');
    localStorage.setItem('guest_view_type', guestType);
    this.events.publish('auth:type-selected');

    try {
      await this.guestAccountService.createGuestAccount(guestType).catch((error) => {
        this.util.showError('Something went wrong. Please try again.');
        throw error;
      });
      localStorage.setItem('hasCard', 'false');
      await this.storage.save('isGuestAccount', true);
      if (guestType === 'rental') {
        await Promise.all([
          this.setOnboardingGoals(),
          this.aboutThisPageService.fetchAndStoreAboutThisPageData()
        ]);
      } else {
        await this.aboutThisPageService.fetchAndStoreAboutThisPageData();
      }
      this.auth.authenticated.set(true);
      this.events.publish('auth:complete');
      this.navCtrl.navigateRoot('properties');
    } catch (err) {
      localStorage.removeItem('isRentalClient');
      localStorage.removeItem('guest_view_type');
      console.error('Failed to create guest account:', err);
      this.navCtrl.navigateRoot('properties');
    }
  }

  private async setOnboardingGoals(retries = 0): Promise<void> {
    try {
      const goalsToPick = await this.onboardingProvider.getGoalsToPickList();
      const goalsPayload = {
        user_goals: goalsToPick.map((goal) => ({
          user_goal_type_id: goal.id,
          chosen: true
        }))
      };
      await this.onboardingProvider.saveChosenGoals(goalsPayload);
      await this.onboardingProvider.getGoalsFromServer();
      localStorage.setItem('showOnboarding', 'true');
      this.events.publish('goals:updated');
    } catch (err) {
      console.error('setOnboardingGoals failed:', err);
      if (retries < 3) {
        setTimeout(() => this.setOnboardingGoals(retries + 1), 5000);
      } else {
        localStorage.removeItem('tryGoalSetup');
      }
    }
  }
}
